import React, { ComponentProps, ComponentType } from 'react';
import { useHBFormField, HBFormFieldBundle } from './useHBFormField';

/**
 * Public props of a field built with `createHBFormField`: the wrapped field's own
 * props, minus the ones Formik supplies (value, onChange, error…), with `id`
 * required — it is the Formik field key.
 */
export type HBFormFieldOf<F extends ComponentType<any>> = Omit<
    ComponentProps<F>,
    keyof HBFormFieldBundle
> & {
    /** Formik field key. Also the `<label htmlFor>` target on the underlying field. */
    id: string;
};

interface CreateHBFormFieldOptions {
    /** React DevTools display name for the generated form field. */
    displayName?: string;
}

/**
 * Wraps a field component (typically the output of `createHBField`) and binds it
 * to the surrounding HBFormProvider via `useHBFormField`. The Formik bundle
 * (value, onChange, onBlur, error, errorMessage, required, maxLength) overrides
 * any matching props the consumer passes.
 *
 * ```tsx
 * const HBInputField = createHBField(HBInput, { defaultControlProps: { isNewHBMode: true } });
 * const HBFormInputField = createHBFormField(HBInputField);
 * // inside an HBFormProvider:
 * <HBFormInputField id="email" label="Email" testId="email" />
 * ```
 *
 * Must be used inside an HBFormProvider — `useHBFormField` throws otherwise.
 */
export function createHBFormField<F extends ComponentType<any>>(
    FieldComponent: F,
    options?: CreateHBFormFieldOptions
) {
    const Field = FieldComponent as ComponentType<any>;

    const HBFormField = (props: HBFormFieldOf<F>) => {
        // setValue is dropped here rather than spread: it's only meaningful for a
        // value-based control wired manually inside a render slot (`onChange={bundle.setValue}`,
        // as in the useHBFormField examples). Field (createHBField's output) has no concept
        // of it — it isn't part of HBFieldControlRenderProps — so it would otherwise fall
        // through Field's chrome/control prop split straight onto the underlying DOM node.
        const { setValue: _setValue, ...bundle } = useHBFormField(props.id);
        return <Field {...props} {...bundle} />;
    };

    HBFormField.displayName =
        options?.displayName ??
        `HBFormField(${
            FieldComponent.displayName || FieldComponent.name || 'Field'
        })`;

    return HBFormField;
}
